'use client';

import { HttpError } from 'http-errors';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useSearchParams } from 'next/navigation';
import { useCallback, useContext } from 'react';

import { toFlagKeys } from '@/consts/LDFlags';
import { VisitorContext, getApiHeaders, getSessionId, getVid, useLDExperiments } from '@/context';
import { publishFromTracking } from '@/embed/hostTracking';
import { isAdCampaign } from '@/helpers/attribution';
import { parseResponse } from '@/helpers/fetch';
import { stripNulls } from '@/helpers/formatters';
import { getUrlMetaValues } from '@/helpers/meta';
import {
  EngagementSignalData,
  EventData,
  AD_EVENTS,
  TRACKING,
  TrackingEvent,
  trackingToFunnelEvent,
  TrackEventFn,
} from '@/helpers/tracking';
import { AffiliateId, deriveAffiliateId, FunnelType, LDUser, MetaValues, Visitor } from '@/models';

import { trackGoogleConversion } from './GoogleTagManager';
import { normalizeExperiments } from './normalizers';
import { useFunnelType } from './useFunnelType';
import { useLDFlags } from './useLDFlags';
import { useLogger } from './useLogger';

type Result = {
  compileMetaValues: (overrides?: MetaValues) => Promise<MetaValues>;
  initTracking: (visitor: Visitor) => Promise<void>;
  trackEvent: TrackEventFn;
  version?: string;
};

export const useTracking = (): Result => {
  const { visitor } = useContext(VisitorContext);

  const flags = useLDFlags();
  const experiments = useLDExperiments();

  const ldClient = useLDClient();
  const { funnelType, version } = useFunnelType();
  const { addAction, addError, setUserProperty } = useLogger();
  const searchParams = useSearchParams();
  const affIdParam = searchParams.get('affid') as AffiliateId | undefined;

  const compileMetaValues = useCallback(
    async (overrides?: MetaValues) => {
      const flagsDashCase = Object.keys(flags).length ? toFlagKeys(flags) : undefined;

      const urlMetaValues = getUrlMetaValues();
      return stripNulls({
        ...urlMetaValues,
        affiliate_id: deriveAffiliateId(affIdParam),
        experiments: experiments.length ? experiments : undefined,
        flags: flagsDashCase,
        source: funnelType,
        version,
        ...overrides,
      });
    },
    [affIdParam, experiments, flags, funnelType, version]
  );

  const trackEvent = useCallback<TrackEventFn>(
    async (eventName: TrackingEvent, eventData?: EventData) => {
      const context = ldClient?.getContext() as LDUser | undefined;
      const vid = getVid();
      if (context?.isTester) {
        // Don't track testers
        return;
      }

      try {
        if (!eventName) {
          throw new Error('Tracking event name is required');
        }

        const funnelEvent = trackingToFunnelEvent[eventName] ?? eventName;
        const ad_event = AD_EVENTS.has(funnelEvent) || null;
        const data: EventData | undefined = eventData || ad_event ? stripNulls({ ...eventData, ad_event }) : undefined;

        await saveVisitorActivity(vid, eventName, data);

        addAction(funnelEvent, data);
        ldClient?.track(funnelEvent, data);

        trackGoogleConversion(eventName, data);

        if (funnelType === FunnelType.EMBED) {
          publishFromTracking(eventName, eventData, visitor?.meta_values?.embed_partner_id);
        }

        const address = data?.address;
        if (address) {
          setUserProperty('address', address);
          ldClient?.identify({ ...context, address });
        }

        if (experiments.length) {
          setUserProperty('experiments', normalizeExperiments(experiments));
        }
      } catch (err) {
        const error = err as HttpError;
        const { message, status } = error ?? {};
        if (status === 401 || message === '401' || message?.includes('Matching session invalid or not found')) {
          return;
        }

        addError(err, { eventData, eventName, visitor });
      }
    },
    [addAction, addError, experiments, funnelType, ldClient, setUserProperty, visitor]
  );

  const initTracking = useCallback(
    async ({ meta_values, vid }: { meta_values?: MetaValues; vid: string }) => {
      const context = ldClient?.getContext() as LDUser | undefined;
      if (!vid || context?.isTester) {
        // Don't track bots or testers
        return;
      }

      // Only track the first load of the visitor
      if (!meta_values?.landing_load_time) {
        trackEvent(TRACKING.VISITOR_LOADED);

        const { channel, landing_page } = meta_values ?? {};
        if (isAdCampaign(channel)) {
          trackEvent(TRACKING.AD_LANDING, { channel, landing_page });
        }
      }
    },
    [ldClient, trackEvent]
  );

  return { compileMetaValues, initTracking, trackEvent, version };
};

export const saveVisitorActivity = async (
  vid: string | null,
  name: TrackingEvent,
  data?: EventData | EngagementSignalData
) => {
  if (!vid || !getSessionId()) {
    return;
  }

  const response = await fetch('/home-solar/api/v1/inp/visitor/activity', {
    body: JSON.stringify({ event_data: data, name, vid }),
    headers: getApiHeaders(),
    keepalive: true, // so browser navigation doesn't kill the request
    method: 'POST',
  });

  await parseResponse(response);
};
